import Papa from 'papaparse';
import { readSheet } from 'read-excel-file/browser';
import writeXlsxFile, { SheetData } from 'write-excel-file/browser';

export const SPREADSHEET_LIMITS = {
    maxColumns: 100,
    maxEntries: 512,
    maxFileBytes: 5 * 1024 * 1024,
    maxRows: 1000,
    maxUncompressedBytes: 32 * 1024 * 1024,
    maxCellCharacters: 20_000,
} as const;

const CSV_EXTENSION = '.csv';
const XLSX_EXTENSION = '.xlsx';
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP_LOCAL_FILE_SIGNATURE = 0x04034b50;
const MAX_ZIP_COMMENT_BYTES = 65_535;

type SpreadsheetRecord = Record<string, unknown>;

export class SpreadsheetSecurityError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SpreadsheetSecurityError';
    }
}

const getFileExtension = (fileName: string): string => {
    const extensionIndex = fileName.lastIndexOf('.');

    return extensionIndex === -1 ? '' : fileName.slice(extensionIndex).toLowerCase();
};

export const validateSpreadsheetFileMetadata = (file: File): void => {
    const extension = getFileExtension(file.name);

    if (extension !== CSV_EXTENSION && extension !== XLSX_EXTENSION) {
        throw new SpreadsheetSecurityError('Select a .csv or .xlsx file. Legacy .xls files are not supported.');
    }

    if (file.size === 0) {
        throw new SpreadsheetSecurityError('The selected spreadsheet is empty.');
    }

    if (file.size > SPREADSHEET_LIMITS.maxFileBytes) {
        throw new SpreadsheetSecurityError('Spreadsheet files must be 5 MB or smaller.');
    }
};

const findEndOfCentralDirectory = (view: DataView): number => {
    const minimumOffset = Math.max(0, view.byteLength - MAX_ZIP_COMMENT_BYTES - 22);

    for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
        if (view.getUint32(offset, true) === ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE) return offset;
    }

    throw new SpreadsheetSecurityError('The Excel file is not a valid ZIP archive.');
};

export const validateXlsxArchive = (buffer: ArrayBuffer): void => {
    const view = new DataView(buffer);

    if (view.byteLength < 22 || view.getUint32(0, true) !== ZIP_LOCAL_FILE_SIGNATURE) {
        throw new SpreadsheetSecurityError('The file contents do not match the .xlsx format.');
    }

    const endOffset = findEndOfCentralDirectory(view);
    const entryCount = view.getUint16(endOffset + 10, true);
    const centralDirectorySize = view.getUint32(endOffset + 12, true);
    const centralDirectoryOffset = view.getUint32(endOffset + 16, true);

    if (entryCount === 0 || entryCount > SPREADSHEET_LIMITS.maxEntries) {
        throw new SpreadsheetSecurityError('The Excel file contains an unsafe number of archive entries.');
    }

    if (centralDirectoryOffset + centralDirectorySize > endOffset) {
        throw new SpreadsheetSecurityError('The Excel archive directory is malformed.');
    }

    const decoder = new TextDecoder('utf-8', { fatal: true });
    let offset = centralDirectoryOffset;
    let totalUncompressedBytes = 0;
    let containsContentTypes = false;
    let containsWorkbook = false;

    for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
        if (offset + 46 > endOffset || view.getUint32(offset, true) !== ZIP_CENTRAL_DIRECTORY_SIGNATURE) {
            throw new SpreadsheetSecurityError('The Excel archive contains a malformed entry.');
        }

        const flags = view.getUint16(offset + 8, true);
        const compressedBytes = view.getUint32(offset + 20, true);
        const uncompressedBytes = view.getUint32(offset + 24, true);
        const fileNameLength = view.getUint16(offset + 28, true);
        const extraLength = view.getUint16(offset + 30, true);
        const commentLength = view.getUint16(offset + 32, true);
        const nextOffset = offset + 46 + fileNameLength + extraLength + commentLength;

        if ((flags & 0x1) !== 0 || compressedBytes === 0xffffffff || uncompressedBytes === 0xffffffff) {
            throw new SpreadsheetSecurityError('Encrypted and ZIP64 Excel archives are not supported.');
        }

        if (nextOffset > endOffset) {
            throw new SpreadsheetSecurityError('The Excel archive entry exceeds its declared boundary.');
        }

        let fileName: string;

        try {
            fileName = decoder.decode(new Uint8Array(buffer, offset + 46, fileNameLength));
        } catch {
            throw new SpreadsheetSecurityError('The Excel archive contains an invalid file name.');
        }

        if (fileName.startsWith('/') || fileName.startsWith('\\') || fileName.split(/[\\/]/u).includes('..')) {
            throw new SpreadsheetSecurityError('The Excel archive contains an unsafe file path.');
        }

        totalUncompressedBytes += uncompressedBytes;

        if (totalUncompressedBytes > SPREADSHEET_LIMITS.maxUncompressedBytes) {
            throw new SpreadsheetSecurityError('The expanded Excel file is too large to process safely.');
        }

        if (compressedBytes === 0 && uncompressedBytes > 0) {
            throw new SpreadsheetSecurityError('The Excel archive contains an invalid compression ratio.');
        }

        if (compressedBytes > 0 && uncompressedBytes / compressedBytes > 1000) {
            throw new SpreadsheetSecurityError('The Excel archive compression ratio is unsafe.');
        }

        containsContentTypes ||= fileName === '[Content_Types].xml';
        containsWorkbook ||= fileName === 'xl/workbook.xml';
        offset = nextOffset;
    }

    if (offset !== centralDirectoryOffset + centralDirectorySize || !containsContentTypes || !containsWorkbook) {
        throw new SpreadsheetSecurityError('The archive is not a valid Excel workbook.');
    }
};

const normalizeSpreadsheetValue = (value: unknown): string | number | boolean | null => {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
    if (typeof value === 'string') return value.slice(0, SPREADSHEET_LIMITS.maxCellCharacters);
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'bigint') return value.toString();

    try {
        return JSON.stringify(value).slice(0, SPREADSHEET_LIMITS.maxCellCharacters);
    } catch {
        return String(value).slice(0, SPREADSHEET_LIMITS.maxCellCharacters);
    }
};

const validateRows = (rows: readonly (readonly unknown[])[]): void => {
    if (rows.length === 0) throw new SpreadsheetSecurityError('The spreadsheet does not contain any rows.');
    if (rows.length - 1 > SPREADSHEET_LIMITS.maxRows) {
        throw new SpreadsheetSecurityError('Spreadsheet imports are limited to 1,000 data rows.');
    }

    const widestRow = rows.reduce((width, row) => Math.max(width, row.length), 0);

    if (widestRow > SPREADSHEET_LIMITS.maxColumns) {
        throw new SpreadsheetSecurityError('Spreadsheet imports are limited to 100 columns.');
    }

    for (const row of rows) {
        for (const cell of row) {
            if (typeof cell === 'string' && cell.length > SPREADSHEET_LIMITS.maxCellCharacters) {
                throw new SpreadsheetSecurityError('A spreadsheet cell exceeds the 20,000-character limit.');
            }
        }
    }
};

const rowsToRecords = (rows: readonly (readonly unknown[])[]): SpreadsheetRecord[] => {
    validateRows(rows);

    const headers = rows[0].map((value) => String(value ?? '').trim());
    const normalizedHeaders = headers.map((header) => header.toLocaleLowerCase());

    if (headers.some((header) => header.length === 0)) {
        throw new SpreadsheetSecurityError('Every imported column must have a header.');
    }

    if (new Set(normalizedHeaders).size !== normalizedHeaders.length) {
        throw new SpreadsheetSecurityError('Spreadsheet column headers must be unique.');
    }

    return rows
        .slice(1)
        .map((row) =>
            Object.fromEntries(headers.map((header, index) => [header, normalizeSpreadsheetValue(row[index])])),
        );
};

const parseCsv = async (file: File): Promise<SpreadsheetRecord[]> => {
    const text = await file.text();

    if (text.includes('\0')) throw new SpreadsheetSecurityError('The CSV file contains binary data.');

    const result = Papa.parse<unknown[]>(text, {
        dynamicTyping: false,
        skipEmptyLines: 'greedy',
    });

    if (result.errors.length > 0) {
        throw new SpreadsheetSecurityError(`The CSV file could not be parsed: ${result.errors[0].message}`);
    }

    return rowsToRecords(result.data);
};

export const parseSpreadsheetFile = async (file: File): Promise<SpreadsheetRecord[]> => {
    validateSpreadsheetFileMetadata(file);

    if (getFileExtension(file.name) === CSV_EXTENSION) return parseCsv(file);

    const buffer = await file.arrayBuffer();
    validateXlsxArchive(buffer);

    const rows = await readSheet(file);

    return rowsToRecords(rows);
};

export const recordsToSpreadsheetRows = (records: readonly SpreadsheetRecord[]): unknown[][] => {
    if (records.length === 0) return [];

    const headers = [...new Set(records.flatMap((record) => Object.keys(record)))];

    return [headers, ...records.map((record) => headers.map((header) => record[header]))];
};

const neutralizeCsvFormula = (value: string | number | boolean | null): string | number | boolean | null =>
    typeof value === 'string' && /^\s*[=+\-@]/u.test(value) ? `'${value}` : value;

export const serializeSpreadsheetRowsToCsv = (rows: readonly (readonly unknown[])[]): string =>
    Papa.unparse(
        rows.map((row) => row.map((value) => neutralizeCsvFormula(normalizeSpreadsheetValue(value)))),
        {
            escapeFormulae: true,
            newline: '\r\n',
        },
    );

const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    link.click();

    setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const downloadSpreadsheet = async (
    rows: readonly (readonly unknown[])[],
    fileName: string,
    format: 'csv' | 'xlsx',
    sheetName = 'Sheet1',
): Promise<void> => {
    if (rows.length === 0) throw new SpreadsheetSecurityError('There is no spreadsheet data to export.');

    const normalizedRows = rows.map((row) => row.map(normalizeSpreadsheetValue));

    if (format === 'csv') {
        const csv = serializeSpreadsheetRowsToCsv(normalizedRows);
        downloadBlob(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }), `${fileName}.csv`);
        return;
    }

    await writeXlsxFile(normalizedRows as SheetData, { sheet: sheetName }).toFile(`${fileName}.xlsx`);
};
